import { useEffect } from 'react';

import { Box, Flex, xcss } from '@atlaskit/primitives';

import { LoomChoiceVariantA } from '@/components/signup/loom-land-opt-in/loom-choice-variant-a';
import { LoomChoiceVariantB } from '@/components/signup/loom-land-opt-in/loom-choice-variant-b';
import { useLoomLandOptIn } from '@/components/signup/loom-land-opt-in/use-loom-land-opt-in';
import { useLocation } from '@/utils/location-provider';

/**
 * TWCG-2108 Loom Land opt-in choice page.
 *
 * Route: /try/cloud/loom/choose
 *
 * Branches on the `twcg2108LoomLandOptIn` multivariant experiment cohort:
 * - control:  fall through to the existing Loom-only signup flow.
 * - radio:    {@link LoomChoiceVariantA} (multi-option "TWC choice" card).
 * - longText: {@link LoomChoiceVariantB} (single-option TWC pitch).
 *
 * This screen is read-only w.r.t. the experiment: it only consumes the resolved
 * cohort to render the correct arm. Exposure is owned by `/try/cloud/signup`,
 * where the treatment (the continue-URL rewrite) applies to the whole Loom-intent
 * population, so this page does NOT fire exposure.
 */

const pageStyles = xcss({
	minHeight: '100vh',
	width: '100%',
	backgroundColor: 'elevation.surface',
});

const centeredStyles = xcss({
	minHeight: '100vh',
	padding: 'space.400',
});

const redirectToLoomOnlySignup = (search: string) => {
	// Control cohort keeps today's behaviour: the standard Loom signup page.
	window.location.replace(`/try/cloud/signup${search}`);
};

export const LoomChoicePageTemplate = () => {
	const { cohort, isResolved, isEnrolled } = useLoomLandOptIn();
	const location = useLocation();

	// Fall through to the Loom-only signup whenever we are not going to show a
	// treatment arm:
	//  - resolved control cohort, or
	//  - definitively not enrolled (indeterminate / not-enrolled — e.g. no Loom
	//    intent or Statsig failed to initialise).
	// Only the transient "enrolled but arm not yet resolved" window renders
	// nothing, so users never get stuck on a blank page.
	const shouldRedirect = (isResolved && cohort === 'control') || !isEnrolled;

	useEffect(() => {
		if (shouldRedirect) {
			redirectToLoomOnlySignup(location.search);
		}
	}, [shouldRedirect, location.search]);

	if (shouldRedirect || !isResolved) {
		// Redirecting (handled in the effect above), or still waiting for Statsig
		// to resolve the arm for an enrolled user; render nothing meanwhile.
		return null;
	}

	return (
		<Box xcss={pageStyles}>
			<Flex xcss={centeredStyles} alignItems="center" justifyContent="center">
				{cohort === 'longText' ? <LoomChoiceVariantB /> : <LoomChoiceVariantA />}
			</Flex>
		</Box>
	);
};
